import environment from '@utilities/environment';
import { useAreaData } from '@utilities/hooks';
import Cookies from 'js-cookie';

import { clearUser, updateUser } from '../store';
import {
    ADD_TO_WISHLIST_ENDPOINT,
    COOKIE_EXPIRES,
    COOKIE_NAME,
    GET_USER_INFO_ENDPOINT,
    REMOVE_FROM_WISHLIST_ENDPOINT,
    SESSION_STORAGE_NAME,
    UPDATE_RABO_PREFERENCE_ENDPOINT,
} from './constants';
import { getUserInfo } from './provider';
import { UserData, UserInfo } from './store.types';

export const fillUserStore = async (userData: UserData | undefined) => {
    if (!userData) return;

    const userInfo: UserInfo = {
        id: userData.Id,
        email: userData.Email ?? '',
        displayName: userData.DisplayName ?? '',
        initials: userData.Initials ?? '',
        firstName: userData.FirstName ?? '',
        lastName: userData.LastName ?? '',
        gender: userData.Salutation,
        subscribedToAreaNewsletter: userData.SubscribedToAreaNewsletter ?? false,
        hasRabobankOpportunity: userData.HasRabobankOpportunity ?? false,
        rabobankPreference: userData.RabobankPreference,
    };
    if (userData.Avatar) userInfo.avatar = userData.Avatar;
    if (userData.HasAddress) userInfo.hasAddress = userData.HasAddress;
    if (userData.PostalCode) userInfo.zipcode = userData.PostalCode;
    if (userData.Street) userInfo.street = userData.Street;
    if (userData.HouseNumber) userInfo.houseNumber = userData.HouseNumber;
    if (userData.HouseNumberAddition) userInfo.houseNumberSuffix = userData.HouseNumberAddition;
    if (userData.City) userInfo.city = userData.City;
    if (userData.PhoneNumber) userInfo.phoneNumber = userData.PhoneNumber;
    if (userData.Country) userInfo.country = userData.Country;

    const { projectPhases, propertyTypes } = await useAreaData();

    userData.Projects = userData.Projects.filter(({ Id }) =>
        projectPhases.find(({ id, mainImage }) => id === Id && !!mainImage?.url),
    );
    userData.PropertyTypes = userData.PropertyTypes.filter(({ Id }) =>
        propertyTypes.find(({ id, mainImage }) => id === Id && !!mainImage?.url),
    );
    updateUser({
        loggedIn: true,
        info: userInfo,
        wishlist: userData.Wishlist,
        areas: userData.Areas,
        projects: userData.Projects,
        propertyTypes: userData.PropertyTypes,
    });
};

export const setMyAccountCookie = (cookieData: string) => {
    Cookies.set(COOKIE_NAME, cookieData, { expires: COOKIE_EXPIRES });
};

export const getMyAccountCookie = () => {
    return Cookies.get(COOKIE_NAME);
};

export const getSessionStorageData = () => {
    return sessionStorage.getItem(SESSION_STORAGE_NAME);
};

export const fetchUserData = async (token: string) => {
    const { areaId } = window.__ENVIRONMENT_DETAILS__;
    const url = `${environment.baseApiUrl}${GET_USER_INFO_ENDPOINT}?token=${token}&areaid=${areaId}`;

    let userData;
    try {
        const response = await fetch(url, {
            method: 'GET',
            cache: 'no-cache',
        });

        userData = (await response.json()) as UserData;
    } catch (error) {
        console.error("Couldn't fetch user data:", '\n', error);
    }

    return userData;
};

export const clearData = () => {
    Cookies.remove(COOKIE_NAME);
    sessionStorage.removeItem(SESSION_STORAGE_NAME);
    clearUser();
};

const makeFormPost = async <T = any>(endpoint: string, bodyParams?: Record<string, string>) => {
    const token = getMyAccountCookie();
    const { id: accountId } = getUserInfo() ?? {};

    let body = '',
        headers = {};

    if (accountId) {
        body = new URLSearchParams({
            token: token ?? '',
            accountId: accountId ?? '',
            ...bodyParams,
        }).toString();
        headers = {
            'x-requested-with': 'XMLHttpRequest',
            'content-type': 'application/x-www-form-urlencoded; charset=UTF-8',
        };
    }

    return (await fetch(endpoint, { method: 'POST', body, headers }).then((r) => r.json())) as T;
};

export const addToWishlistApiCall = (constructionNumberId: string) => {
    return makeFormPost(ADD_TO_WISHLIST_ENDPOINT, { constructionNumberId });
};

export const removeFromWishlistApiCall = (constructionNumberId: string) => {
    return makeFormPost(REMOVE_FROM_WISHLIST_ENDPOINT, { constructionNumberId });
};

export const updateRabobankPreferenceApiCall = () => {
    const { areaId } = window.__ENVIRONMENT_DETAILS__;
    return makeFormPost<boolean>(UPDATE_RABO_PREFERENCE_ENDPOINT, { areaId });
};
